import React, { useEffect, useState } from 'react';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import {
    applyReevaluation,
    continueReevaluation,
    getReevaluation,
    getReevaluations,
    nextReevaluationBatch,
    ReevaluationRun,
    ReevaluationSummary,
    startReevaluation,
} from '../../api/reevaluation';
import { CockpitEnvironment } from '../data/cockpit';
import { envKind, useCockpit } from '../data/CockpitContext';
import { clock, number, when } from '../data/format';
import { apiError, usePoll } from '../data/usePoll';
import { Card, CheckRow, Empty, EnvChip, Mark, Spinner } from '../components/ui';

const BATCH = 500;

const matches = (s: ReevaluationSummary, e: CockpitEnvironment) => {
    const scope = s.scope.toLowerCase();
    return scope.includes(e.customer.toLowerCase()) && scope.includes(e.environment.toLowerCase());
};

/** How long after a deploy a re-classification still counts as the one for that deploy, as the list below keeps it. */
const RECENT_MS = 3 * 86400000;

/**
 * The environments whose backlog was re-classified since the deploy, keyed "customer/environment" in lower case: the
 * latest run is applied, or finished with nothing to change. Their issues are no longer in the old classification.
 */
export const reclassified = (runs: ReevaluationSummary[] | undefined, environments: CockpitEnvironment[], now = Date.now()): Set<string> =>
    new Set(
        environments
            .filter((e) => {
                const latest = (runs ?? []).filter((r) => matches(r, e)).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
                if (!latest || now - new Date(latest.createdAt).getTime() > RECENT_MS) return false;
                return latest.state === 'Applied' || (latest.state === 'Done' && latest.toClose + latest.toReopen + latest.toReclassify === 0);
            })
            .map((e) => `${e.customer}/${e.environment}`.toLowerCase())
    );

/**
 * One environment after a deploy: step 1 re-classifies the backlog with the new build's rules, newest
 * first and then the older backlog in batches, reading only; step 2 applies what a person confirms.
 */
const EnvironmentInventory = ({ env, latest }: { env: CockpitEnvironment; latest?: ReevaluationSummary }) => {
    const axios = useAxiosSwagger();
    const [run, setRun] = useState<ReevaluationRun>();
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!axios || !latest || run) return;
        getReevaluation(axios, latest.runId).then(setRun).catch(() => undefined);
    }, [axios, latest, run]);

    // A dry run goes a step at a time: keep stepping while it runs and is not waiting for Exact's new day.
    useEffect(() => {
        if (!axios || !run || run.complete || run.state !== 'Running' || run.pausedUntil) return;
        const timer = setTimeout(async () => {
            try {
                setRun(await continueReevaluation(axios, run.runId));
            } catch (e: any) {
                setError(apiError(e));
            }
        }, 1500);
        return () => clearTimeout(timer);
    }, [axios, run]);

    // Applying also goes in parts.
    useEffect(() => {
        if (!axios || !run || run.state !== 'Applying' || run.apply?.complete) return;
        const timer = setTimeout(async () => {
            try {
                setRun(await applyReevaluation(axios, run.runId));
            } catch (e: any) {
                setError(apiError(e));
            }
        }, 1000);
        return () => clearTimeout(timer);
    }, [axios, run]);

    const act = async (what: () => Promise<ReevaluationRun>) => {
        setBusy(true);
        setError(undefined);
        try {
            setRun(await what());
        } catch (e: any) {
            setError(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    const report = run?.report;
    const total = run?.total ?? undefined;
    const processed = run?.processed ?? 0;
    const pct = total ? (processed / total) * 100 : run?.complete ? 100 : 0;
    const applied = run?.state === 'Applied';
    const state = !run
        ? 'Nog niet geïnventariseerd'
        : applied
          ? 'Toegepast'
          : run.state === 'Applying'
            ? 'Toepassen…'
            : run.pausedUntil
              ? `Gepauzeerd tot ${clock(run.pausedUntil)}`
              : run.complete
                ? 'Klaar om toe te passen'
                : 'Inventariseert';
    const chip = applied ? 'chip ok' : run?.pausedUntil ? 'chip warn' : run?.complete ? 'chip ok' : run ? 'chip info' : 'chip';

    return (
        <Card
            title={
                <div className="hrow">
                    <b style={{ fontSize: 16 }}>{env.customer}</b>
                    <EnvChip kind={envKind(env)} environment={env.environment} />
                    {env.build && <span className="mono mut" style={{ fontSize: 12 }}>build {env.build}</span>}
                </div>
            }
            right={<span className={chip}>{run && !run.complete && !run.pausedUntil && run.state === 'Running' && <Spinner />}{state}</span>}
        >
            <div className="cb g2" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.3fr)', gap: 24 }}>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>Stap 1 · Inventariseren</div>
                    {!run && (
                        <>
                            <div className="mut" style={{ fontSize: 13, marginBottom: 10 }}>
                                {number(env.open)} open issues, plus wat de laatste 30 dagen sloot. De nieuwste {BATCH} eerst; leest alleen.
                            </div>
                            <button className="btn pri" disabled={busy} onClick={() => act(() => startReevaluation(axios!, { customer: env.customer, environment: env.environment, limit: BATCH }))}>
                                Inventariseren
                            </button>
                        </>
                    )}
                    {run && (
                        <>
                            <div className="hrow mono" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                                <span>
                                    {number(processed)} {total ? `van ${number(total)}` : ''} bekeken
                                </span>
                                <span className="mut">{run.complete && !run.more ? 'alles bekeken' : run.continuesRun ? 'oudere achterstand' : 'nieuwste eerst'}</span>
                            </div>
                            <div className="meter" style={{ height: 10, marginTop: 6 }}>
                                <b className="i" style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                            <div className="hrow mut" style={{ fontSize: 12, marginTop: 6 }}>
                                <span>Exact-calls {number(run.exactCalls)}</span>
                                <span>gestart {when(run.createdAt)}</span>
                            </div>
                            <div className="hrow" style={{ marginTop: 12 }}>
                                {run.more && !run.nextBatch && run.complete && (
                                    <button className="btn sm" disabled={busy} onClick={() => act(() => nextReevaluationBatch(axios!, run.runId))}>
                                        Volgende batch · {BATCH} ouder
                                    </button>
                                )}
                                {run.pausedUntil && <span className="chip warn">Exact-daglimiet, verder om {clock(run.pausedUntil)}</span>}
                            </div>
                        </>
                    )}
                </div>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>Uitkomst</div>
                    {!report && <Empty>Nog niets bekeken.</Empty>}
                    {report && (
                        <div className="checks">
                            <CheckRow tone="ok" label="Opgelost met de nieuwe indeling" value={number(report.toClose)} />
                            <CheckRow tone="warn" mark="↺" label="Anders ingedeeld" value={number(report.toReclassify)} />
                            <CheckRow tone="bad" mark="↑" label="Weer open" value={number(report.toReopen)} />
                            <CheckRow tone="todo" label="Ongewijzigd" value={number(report.unchanged + report.keepOpen)} />
                            {report.unknown > 0 && <CheckRow tone="wait" label="Niet te beoordelen" value={number(report.unknown)} />}
                        </div>
                    )}
                    <div className="hrow" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                        {applied && run?.apply && (
                            <span className="chip ok">
                                <Mark tone="ok" />
                                {run.apply.closed} gesloten · {run.apply.reclassified} bijgewerkt
                            </span>
                        )}
                        {run && run.complete && !applied && run.state !== 'Applying' && report && (
                            <button className="btn pri" disabled={busy} onClick={() => act(() => applyReevaluation(axios!, run.runId))}>
                                Stap 2 · Toepassen
                            </button>
                        )}
                    </div>
                    {error && <div className="bad" style={{ fontSize: 13, marginTop: 8 }}>{error}</div>}
                </div>
            </div>
        </Card>
    );
};

export const AfterDeploy = ({ customer, environment }: { customer?: string; environment?: string }) => {
    const axios = useAxiosSwagger();
    const { environments, find } = useCockpit();
    const runs = usePoll<ReevaluationSummary[]>(axios ? (s) => getReevaluations(axios, s) : null, 30000, [axios]);

    const scoped = customer && environment ? find(customer, environment) : undefined;
    const candidates = scoped
        ? [scoped]
        : environments.filter((e) => e.watchStatus === 'watching' || (runs.data ?? []).some((r) => matches(r, e) && Date.now() - new Date(r.createdAt).getTime() < RECENT_MS));

    return (
        <>
            <Card>
                <div className="cb g3" style={{ gap: 24 }}>
                    <div className="hrow" style={{ alignItems: 'flex-start', gap: 12 }}>
                        <Mark tone="wait">1</Mark>
                        <div><b>Inventariseren</b><div className="mut" style={{ fontSize: 13 }}>Opnieuw indelen met de regels van de nieuwe build. Alleen lezen.</div></div>
                    </div>
                    <div className="hrow" style={{ alignItems: 'flex-start', gap: 12 }}>
                        <Mark tone="wait">2</Mark>
                        <div><b>Nieuwste eerst</b><div className="mut" style={{ fontSize: 13 }}>Daarna in batches van {BATCH} de oudere achterstand.</div></div>
                    </div>
                    <div className="hrow" style={{ alignItems: 'flex-start', gap: 12 }}>
                        <Mark tone="wait">3</Mark>
                        <div><b>Toepassen</b><div className="mut" style={{ fontSize: 13 }}>Jij bevestigt: sluiten wat opgelost is, bijwerken wat anders is.</div></div>
                    </div>
                </div>
            </Card>
            {runs.error && <div className="notice">{runs.error}</div>}
            {candidates.length === 0 && <Empty>Geen omgeving met een recente deploy. Kies een omgeving om toch te inventariseren.</Empty>}
            {candidates.map((e) => (
                <EnvironmentInventory
                    key={`${e.customer}/${e.environment}`}
                    env={e}
                    latest={(runs.data ?? []).filter((r) => matches(r, e)).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]}
                />
            ))}
        </>
    );
};
